import { useEffect } from 'react'
import { useForm, useFormContext } from 'react-hook-form'

import { MedicalActTemplate, CreateMedicalActTemplateDto, UpdateMedicalActTemplateDto } from '@/core/models/medical-act.model'
import {
    useCreateMedicalActTemplateMutation,
    useUpdateMedicalActTemplateMutation,
} from '@/core/services/medical-act'

export type MedicalActTemplateFormData = {
    name: string
    code: string
    price: number
    description: string
    icd10Code: string
    icd10Description: string
    isGlobal: boolean
    isActive: boolean
}

const defaultFormValues: MedicalActTemplateFormData = {
    name: '',
    code: '',
    price: 0,
    description: '',
    icd10Code: '',
    icd10Description: '',
    isGlobal: false,
    isActive: true,
}

export type UseMedicalActTemplateFormParams = {
    template: MedicalActTemplate | null
    onSuccess?: () => void
}

export function useMedicalActTemplateForm({ template, onSuccess }: UseMedicalActTemplateFormParams) {
    const isEditing = !!template

    // Mutations
    const [createTemplate, { isLoading: isCreating, error: createError }] = useCreateMedicalActTemplateMutation()
    const [updateTemplate, { isLoading: isUpdating, error: updateError }] = useUpdateMedicalActTemplateMutation()

    // Form setup
    const rhForm = useForm<MedicalActTemplateFormData>({
        defaultValues: defaultFormValues,
    })
    const { reset } = rhForm

    // Reset form when template changes
    useEffect(() => {
        if (template) {
            reset({
                name: template.name,
                code: template.code,
                price: template.price,
                description: template.description || '',
                icd10Code: template.icd10Code || '',
                icd10Description: template.icd10Description || '',
                isGlobal: template.isGlobal,
                isActive: template.isActive,
            })
        } else {
            reset(defaultFormValues)
        }
    }, [template, reset])

    const handleSubmit = async (data: MedicalActTemplateFormData) => {
        const dto: CreateMedicalActTemplateDto | UpdateMedicalActTemplateDto = {
            name: data.name,
            code: data.code,
            price: data.price,
            description: data.description || undefined,
            icd10Code: data.icd10Code || undefined,
            icd10Description: data.icd10Description || undefined,
            isGlobal: data.isGlobal,
            isActive: data.isActive,
        }

        try {
            if (isEditing && template) {
                await updateTemplate({ id: template._id, changes: dto }).unwrap()
            } else {
                await createTemplate(dto as CreateMedicalActTemplateDto).unwrap()
            }
            onSuccess?.()
        } catch (error) {
            // Error handled by RTK Query
        }
    }

    const isLoading = isCreating || isUpdating
    const error = createError || updateError

    return {
        rhForm,
        handleSubmit,
        isEditing,
        isLoading,
        error,
    }
}

export function useMedicalActTemplateFormContext() {
    return useFormContext<MedicalActTemplateFormData>()
}
